import './ModalSidebar.scss';
import { FC, JSX, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { overlayActions } from '../../../store/slices/overlay';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * @description ModalSidebar component that displays content with a header and close button.
 * When closed, navigates to the path stored in the overlay state if available.
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @component
 * @param {Object} props - Component props
 * @param {JSX.Element} props.children - Content to be displayed inside the modal sidebar
 * @param {string} props.title - Title to display in the header (will be translated)
 * @param {boolean} [props.fullWidth=false] - Whether the sidebar should take full width
 *
 * @returns {JSX.Element} The rendered ModalSidebar component
 */
const ModalSidebar: FC<{
  children: JSX.Element;
  title: string;
  fullWidth?: boolean;
}> = ({ children, title, fullWidth = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const path = useSelector((state: any) => state.overlay.path);

  /**
   * @description Cleanup effect to close the overlay when component unmounts
   */
  useEffect(() => {
    return () => {
      dispatch(overlayActions.close());
    };
  }, [dispatch]);

  /**
   * @description Handles the close button click.
   * Navigates to the path from overlay state if available and closes the overlay.
   *
   * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @returns {void}
   */
  const handleClose = () => {
    if (path) {
      navigate(path);
      dispatch(overlayActions.close());
    }
  };

  return (
    <div
      className={`modal-sidebar ${fullWidth ? 'full' : ''}`}
      data-testid={`${fullWidth ? 'full' : ''}`}
    >
      <div className="modal-sidebar-header">
        {t(title)}
        <span
          className="modal-sidebar-header__icon"
          data-testid="close-btn"
          onClick={handleClose}
        >
          <IoMdClose size={20} />
        </span>
      </div>
      {children}
    </div>
  );
};

export default ModalSidebar;
